/**
 * bearerFetchPatch.ts — Global window.fetch interceptor for Bearer token injection.
 *
 * WHY THIS EXISTS:
 *   The @workspace/api-client-react customFetch already injects Bearer tokens via
 *   setAuthTokenGetter(). But the query hooks (use-opportunities, use-dashboard,
 *   use-events, mission-control, use-settings) all use raw window.fetch() — they
 *   never go through customFetch. Raw fetch calls have no Authorization header,
 *   so they always hit the api-server as cookie-only requests. On Supabase users
 *   (no cookie session), every one of those routes returns COOKIE_MISSING 401.
 *
 * HOW IT WORKS:
 *   installBearerFetchPatch() MUST be called in main.tsx BEFORE installCsrfInterceptor().
 *   The CSRF interceptor captures window.fetch as its `originalFetch` at install time.
 *   By installing the Bearer patch first, the CSRF interceptor's `originalFetch` is
 *   already Bearer-patched — so even CSRF Layer-2 retry requests carry the JWT.
 *
 *   The token getter is registered separately via setBearerTokenGetter() which is
 *   called from supabaseClient.ts (inside the React module graph, after main.tsx).
 *   The patch is installed with a null getter initially; it becomes live as soon as
 *   supabaseClient.ts is imported (which happens synchronously during the first React
 *   render, before any useEffect fires and thus before any query fires).
 *
 * SAFETY:
 *   - Only injects for /api/ paths — external URLs are never touched.
 *   - Skips injection if Authorization is already present (no double-header).
 *   - Falls back silently if the token getter throws (request proceeds without token).
 *   - Idempotent: calling install() twice is a no-op.
 */

let _tokenGetter: (() => Promise<string | null>) | null = null;

/**
 * Register the async function that returns the current Bearer access token.
 * Called by supabaseClient.ts at module load time.
 */
export function setBearerTokenGetter(getter: () => Promise<string | null>): void {
  _tokenGetter = getter;
}

let _installed = false;

/**
 * Patch window.fetch to inject Authorization: Bearer <token> on all /api/ requests.
 * Must be called BEFORE installCsrfInterceptor() so the CSRF interceptor's
 * internal originalFetch reference is already Bearer-patched.
 */
export function installBearerFetchPatch(): void {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function bearerPatchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    // Resolve URL string from any RequestInfo/URL variant.
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
        ? input.href
        : (input as Request).url;

    // Only intercept /api/ requests when a token getter is registered.
    const isApiCall = url.startsWith('/api/') || url.includes('/api/');

    if (isApiCall && _tokenGetter) {
      // Build a mutable Headers from whatever was passed in.
      const base =
        init?.headers ??
        (input instanceof Request ? input.headers : undefined);
      const headers = new Headers(base);

      if (!headers.has('authorization')) {
        try {
          const token = await _tokenGetter();
          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
            return originalFetch(input, { ...(init ?? {}), headers });
          }
          // token is null — no active session; proceed without Bearer.
          // The route will return 401 if it requires auth, which react-query
          // surfaces as a visible error rather than a silent empty state.
        } catch (err) {
          // Token getter threw — log for diagnostics but do not block the
          // request.  The server will return 401 if auth is required, which
          // produces a visible error rather than a silent COOKIE_MISSING 401
          // that would be swallowed and show an empty dashboard.
          console.warn('[bearer-patch] Token getter threw; sending without Bearer:', err);
        }
      }
    }

    return originalFetch(input, init);
  };
}
